import moment from "moment-timezone";
import { ACTIVITY_FIELD_KEY } from "../constants";
import { unwrapDoubleWrappedParagraph } from "../../../../common/normalizers/richText";

const NON_TEMPLATE_KEYS = new Set([
  ACTIVITY_FIELD_KEY.TITLE,
  ACTIVITY_FIELD_KEY.OWNER,
  ACTIVITY_FIELD_KEY.DUE_DATE,
  ACTIVITY_FIELD_KEY.DUE_DATE_TZ,
  ACTIVITY_FIELD_KEY.DESCRIPTION,
  ACTIVITY_FIELD_KEY.SKILLSET,
  ACTIVITY_FIELD_KEY.ASSIGNMENT,
]);

function extractAssignmentData(activityData) {
  const ownerOrgId = activityData?.owner_org_id;
  const assigneeOrgId = activityData?.assignee_org_id;
  const assigneeUserId = activityData?.current_assignee?.user_id;
  if (
    assigneeUserId !== undefined &&
    assigneeUserId !== null &&
    ownerOrgId !== undefined &&
    ownerOrgId !== null &&
    String(ownerOrgId) === String(assigneeOrgId)
  ) {
    return {
      assignment_method: "teamMember",
      assignMethodId: assigneeUserId,
    };
  }
  if (assigneeOrgId !== undefined && assigneeOrgId !== null) {
    return {
      assignment_method: "b2b",
      assignMethodId: assigneeOrgId,
    };
  }

  // Organization/Contract assignment (if applicable)
  // Need to add logic here if later we have contract assignments

  return null;
}

function getTemplateFieldValue(templateFields, field) {
  const fieldKey = field.fieldKey || field.FieldKey;
  const mapFieldKey = field.mapFieldKey || field.MapFieldKey;

  if (mapFieldKey && Object.prototype.hasOwnProperty.call(templateFields, mapFieldKey)) {
    return templateFields[mapFieldKey];
  }

  if (Object.prototype.hasOwnProperty.call(templateFields, fieldKey)) {
    return templateFields[fieldKey];
  }

  const stripped = fieldKey.replace(
    /_(?:text|string|number|zip|state|county|city|country|singlechoicelist|multiplechoicelist|date|datetime|file|richtext|checkbox|tag|email|decimal|boolean|geopoint|timezone|address|name|datetimetimezone|datewithtimezone|datewithusertimezone|assignmentmethod|richtexteditor)(?:_[a-f0-9]{8})?$/i,
    ''
  );
  if (stripped !== fieldKey && Object.prototype.hasOwnProperty.call(templateFields, stripped)) {
    return templateFields[stripped];
  }

  return undefined;
}

export function buildEditInitialValues(activityData, fields = []) {
  if (!activityData) return {};

  const initialValues = {};

  initialValues[ACTIVITY_FIELD_KEY.TITLE] = activityData.activity_title || activityData.Title || '';
  initialValues[ACTIVITY_FIELD_KEY.DESCRIPTION] = unwrapDoubleWrappedParagraph(activityData.activity_description || activityData.Description || '');

  if (activityData.current_owner.user_id != null) {
    initialValues[ACTIVITY_FIELD_KEY.OWNER] = String(activityData.current_owner.user_id);
  }

  if (activityData.due_date || activityData.dueDate) {
    const timestamp = activityData.due_date || activityData.dueDate;
    if (typeof timestamp === 'number' && timestamp > 0) {
      initialValues[ACTIVITY_FIELD_KEY.DUE_DATE] = moment.unix(timestamp).format('MM/DD/YYYY hh:mm A');
    } else if (typeof timestamp === 'string') {
      initialValues[ACTIVITY_FIELD_KEY.DUE_DATE] = timestamp;
    }
  }

  // NOTE: Need to check if we need this or can remove
  // if (activityData.due_date_tz || activityData.dueDateTz) {
  //   initialValues[ACTIVITY_FIELD_KEY.DUE_DATE_TZ] = tzValue;
  // }

  if (Array.isArray(activityData.skillset) && activityData.skillset.length > 0) {
    initialValues[ACTIVITY_FIELD_KEY.SKILLSET] = activityData.skillset.map((s) => {
      if (typeof s === 'string') {
        return { id: s, text: s };
      }
      return {
        id: String(s.id ?? s.name ?? s.text),
        text: s.name ?? s.text ?? String(s.id),
      };
    });
  }

  const assignmentData = extractAssignmentData(activityData);
  if (assignmentData) {
    initialValues[ACTIVITY_FIELD_KEY.ASSIGNMENT] = {
      assignment_method: assignmentData.assignment_method,
      assignMethodId: assignmentData?.assignMethodId,
    };
  }

  const templateFields = activityData.template_fields || {};

  fields.forEach((field) => {
    const fieldKey = field.fieldKey || field.FieldKey;
    if (!fieldKey || NON_TEMPLATE_KEYS.has(fieldKey)) return;

    const value = getTemplateFieldValue(templateFields, field);
    if (value === null || value === undefined) return;

    const fieldType = String(field.fieldType || field.FieldType || '').toLowerCase();

    if (fieldType === 'date' || fieldType === 'datetime') {
      if (typeof value === 'number' && value > 0) {
        initialValues[fieldKey] = moment.unix(value).format('MM/DD/YYYY');
      } else {
        initialValues[fieldKey] = value;
      }
    } else if (fieldType === 'file') {
      initialValues[fieldKey] = Array.isArray(value) ? value : [];
    } else if (fieldType === 'multiplechoicelist' || fieldType === 'tag') {
      initialValues[fieldKey] = Array.isArray(value) ? value : [value];
    } else {
      initialValues[fieldKey] = value;
    }
  });


  if (Array.isArray(activityData.FnolActFile) && activityData.FnolActFile.length > 0) {
    const fileField = fields.find((f) => {
      const fieldType = String(f.fieldType || f.FieldType || '').toLowerCase();
      return fieldType === 'file';
    });

    if (fileField && fileField.fieldKey) {
      initialValues[fileField.fieldKey] = activityData.FnolActFile.map((file) => ({
        file_id: file.file_id,
        name: file.file_name,
        guid: file.guid,
        url: file.file_url,
        caption: file.caption || '',
      }));
    }
  }

  return initialValues;
};